/**
 * Versão em execução, no pé do menu lateral.
 *
 * POR QUE ISTO É ÚTIL, e não enfeite: no primeiro incidente a pergunta é sempre
 * "o que exatamente está rodando aí?". Sem esta linha, a resposta depende de
 * alguém com acesso ao servidor. Com ela, qualquer pessoa na chamada lê em voz
 * alta a versão e o commit — e é assim que se descobre que o navegador de alguém
 * está com uma versão antiga em cache.
 *
 * A informação vem do SERVIDOR (`GET /version`), não da build do navegador. É
 * deliberado: o que interessa saber é qual API está atendendo. Uma constante
 * embutida no pacote diria o que o navegador baixou, que pode ser outra coisa.
 *
 * Rota pública e sem segredo: `version`, `commit`, momento da build, ambiente e
 * fuso. Nada aqui depende de sessão, então a linha aparece mesmo quando a sessão
 * expira — justamente quando alguém precisa dela.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';

interface BuildInfo {
  version: string;
  commit: string | null;
  builtAt: string | null;
  environment: string;
  timezone: string;
}

/** Commit curto, como o Git exibe. */
function commitCurto(commit: string | null): string | null {
  return commit === null ? null : commit.slice(0, 7);
}

export function VersionBadge(): JSX.Element | null {
  const build = useQuery({
    queryKey: ['version'],
    queryFn: () => api.get<BuildInfo>('/version', undefined, { absolute: true }),
    // A build não muda enquanto a página está aberta. Recarregá-la a cada foco
    // seria requisição sem propósito — e, quando ela MUDA, a publicação derruba
    // o processo e a página é recarregada de todo modo.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  if (build.data === undefined) return null;

  const { version, commit, builtAt, environment } = build.data;
  const curto = commitCurto(commit);

  // O detalhe completo fica no `title`: a linha precisa caber em 240 px, e a
  // pergunta "qual commit?" só aparece quando algo já deu errado.
  const detalhe = [
    `Versão ${version}`,
    commit === null ? null : `commit ${commit}`,
    builtAt === null ? null : `publicado em ${formatDateTime(builtAt)}`,
    `ambiente ${environment}`,
  ]
    .filter((parte) => parte !== null)
    .join(' · ');

  return (
    <p
      className="truncate px-2 pb-1 text-2xs text-brand-300"
      title={detalhe}
      data-testid="app-version"
    >
      <span className="tabular-nums">v{version}</span>
      {curto !== null && <span className="tabular-nums"> · {curto}</span>}
      {/* Ambiente só aparece quando NÃO é produção: ver "development" no rodapé
          é o aviso de que aquela aba não é o sistema real. */}
      {environment !== 'production' && (
        <span className="ml-1 uppercase tracking-wide">{environment}</span>
      )}
    </p>
  );
}
